import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { TrendingUp, TrendingDown, DollarSign, Percent, Building2, Download, CheckCircle, XCircle, Users, Calendar, Clock, Briefcase, Image, FileSpreadsheet, Infinity, Presentation, Calculator, X, Palette } from 'lucide-react';
import { formatCurrency, formatPercent, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, Area, AreaChart, PieChart, Pie, Cell, LabelList, ReferenceLine, ComposedChart
} from 'recharts';

const CHART_COLORS = ['#3B82F6', '#0EA5E9', '#10B981', '#F59E0B', '#8B5CF6'];

export const DashboardPage = () => {
  const { projectId } = useParams();
  const { currentProject, fetchProject, getDashboard, getCalculation } = useProject();
  const { showLabels, formatChartValue } = useChartPreferences();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingFormula, setExportingFormula] = useState(false);
  const [showPptxModal, setShowPptxModal] = useState(false);
  const [exportingPptx, setExportingPptx] = useState(false);
  const [pptxSettings, setPptxSettings] = useState({ primary_color: '#002060', accent_color: '#0066CC', logo_url: '' });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!currentProject || currentProject.id !== projectId) {
          await fetchProject(projectId);
        }
        const data = await getDashboard(projectId);
        setDashboardData(data);
      } catch (err) {
        console.error('Error loading dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId, currentProject, fetchProject, getDashboard]);

  if (loading || !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando dashboard...</div>
      </div>
    );
  }

  const { valuation, income_statement, cash_flow, project, valuation_without_perpetuity } = dashboardData;
  const hasData = project?.investments?.length > 0;
  const perpEnabled = valuation?.perpetuity?.enabled;
  const terminalValue = valuation?.perpetuity?.terminal_value || 0;

  // Prepare chart data
  const incomeChartData = income_statement?.years?.map(year => ({
    year: `Año ${year.year}`,
    Ingresos: year.revenue,
    Gastos: year.cogs + year.operating_expenses + year.depreciation + year.interest_expense
  })) || [];

  const cashFlowChartData = cash_flow?.cash_flows?.map(cf => {
    const val = cf.cumulative_project_cf ?? cf.cumulative_cash_flow;
    return {
      year: cf.year === 0 ? 'Año 0' : `Año ${cf.year}`,
      value: val,
    };
  }) || [];

  // Use payback from valuation (based on project cash flow, consistent with other pages)
  const paybackPeriod = valuation?.payback_period;
  const breakEvenYear = paybackPeriod ? Math.ceil(paybackPeriod) : null;

  // Export PDF handler
  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await fetch(`${API}/api/projects/${projectId}/report/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!response.ok) throw new Error('Error al generar PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'reporte_ejecutivo.pdf';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte PDF descargado');
    } catch (err) {
      toast.error('Error al exportar PDF: ' + err.message);
    } finally {
      setExportingPdf(false);
    }
  };

  // Export Formula Excel handler
  const handleExportFormulaExcel = async () => {
    setExportingFormula(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/projects/${projectId}/export-excel-formula`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!response.ok) throw new Error('Error al generar el archivo');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Modelo_Formulado.xlsx';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Modelo Formulado Excel descargado');
    } catch (err) {
      toast.error('Error al exportar: ' + err.message);
    } finally {
      setExportingFormula(false);
    }
  };

  // Export PowerPoint handler
  const handleExportPptx = async () => {
    setExportingPptx(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/projects/${projectId}/export-pptx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify(pptxSettings)
      });
      if (!response.ok) throw new Error('Error al generar presentación');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Presentacion.pptx';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Presentación descargada');
      setShowPptxModal(false);
    } catch (err) {
      toast.error('Error al exportar: ' + err.message);
    } finally {
      setExportingPptx(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Project Header with Photo and Title */}
      <Card className="bg-gradient-to-r from-primary/10 via-background to-cyan-500/10 border-primary/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            {/* Project Photo */}
            <div className="w-24 h-24 rounded-xl bg-secondary/50 flex items-center justify-center overflow-hidden border-2 border-primary/30">
              {project?.specifications?.logo ? (
                <img src={project.specifications.logo} alt={project?.name} className="w-full h-full object-cover" />
              ) : (
                <Briefcase className="w-10 h-10 text-muted-foreground" />
              )}
            </div>
            
            {/* Project Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                {project?.name || 'Proyecto sin nombre'}
              </h1>
              <p className="text-muted-foreground mt-1">
                {project?.specifications?.project_life_years || 5} años de vida del proyecto • {project?.specifications?.operates_seven_days ? '7 días/semana' : '6 días/semana'}
              </p>
            </div>

            {/* Quick Stats */}
            <div className="flex gap-4">
              <div className="text-center p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Inversión Total</p>
                <p className="text-lg font-bold">{formatCurrency(project?.financing?.total_investment || 0, project?.specifications?.currency || 'MXN')}</p>
              </div>
              <div className="text-center p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <p className="text-xs text-blue-400">Deuda</p>
                <p className="text-lg font-bold text-blue-400">{formatCurrency(project?.financing?.debt_amount || 0, project?.specifications?.currency || 'MXN')}</p>
              </div>
              <div className="text-center p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                <p className="text-xs text-green-400">Capital</p>
                <p className="text-lg font-bold text-green-400">{formatCurrency(project?.financing?.equity_amount || 0, project?.specifications?.currency || 'MXN')}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards - 4 Main Metrics */}
      <div className="grid grid-cols-4 gap-4">
        {/* TIR Card */}
        <Card className="bg-gradient-to-br from-blue-950/50 to-background border-blue-500/30">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-blue-400/80 font-medium mb-1">TIR del Proyecto</p>
                <h3 className="text-3xl font-bold">{hasData ? formatPercent(valuation?.irr || 0) : '--'}</h3>
                {hasData && valuation?.irr_vs_wacc && (
                  <p className={cn(
                    "text-xs mt-2 flex items-center gap-1",
                    valuation.irr_vs_wacc > 0 ? "text-success" : "text-destructive"
                  )}>
                    {valuation.irr_vs_wacc > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {valuation.irr_vs_wacc > 0 ? '+' : ''}{formatPercent(valuation.irr_vs_wacc)} vs WACC
                  </p>
                )}
              </div>
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", valuation?.irr > (project?.financing?.wacc || 0) ? "bg-blue-500/20" : "bg-red-500/20")}>
                <Percent className={cn("w-5 h-5", valuation?.irr > (project?.financing?.wacc || 0) ? "text-blue-400" : "text-red-400")} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* VAN Card */}
        <Card className="bg-gradient-to-br from-green-950/50 to-background border-green-500/30">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-green-400/80 font-medium mb-1">VAN (NPV)</p>
                <h3 className="text-3xl font-bold">{hasData ? formatCurrency(valuation?.npv || 0, project?.specifications?.currency || 'MXN') : '--'}</h3>
                {hasData && (
                  <p className={cn(
                    "text-xs mt-2",
                    valuation?.npv > 0 ? "text-success" : "text-destructive"
                  )}>
                    {valuation?.npv > 0 ? '✓ Genera valor' : '✗ Destruye valor'}
                  </p>
                )}
              </div>
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", valuation?.npv > 0 ? "bg-green-500/20" : "bg-red-500/20")}>
                <DollarSign className={cn("w-5 h-5", valuation?.npv > 0 ? "text-green-400" : "text-red-400")} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WACC Card */}
        <Card className="bg-gradient-to-br from-cyan-950/50 to-background border-cyan-500/30">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-cyan-400/80 font-medium mb-1">WACC</p>
                <h3 className="text-3xl font-bold">{formatPercent(project?.financing?.wacc || 0)}</h3>
                <p className="text-xs mt-2 text-muted-foreground">
                  Costo ponderado de capital
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payback Card */}
        <Card className="bg-gradient-to-br from-amber-950/50 to-background border-amber-500/30">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-amber-400/80 font-medium mb-1">Payback</p>
                <h3 className="text-3xl font-bold">{paybackPeriod ? `${paybackPeriod.toFixed(1)} años` : '--'}</h3>
                <p className="text-xs mt-2 text-muted-foreground">
                  Período de recuperación
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Perpetuity Info Banner */}
      {hasData && perpEnabled && terminalValue !== 0 && (
        <Card className="border-violet-500/30 bg-gradient-to-r from-violet-950/20 to-background" data-testid="dashboard-perpetuity-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Infinity className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Este proyecto incluye Perpetuidad (Valor Terminal)</h3>
                <p className="text-xs text-muted-foreground">Los KPIs de arriba ya incluyen el valor terminal. Aqui puedes comparar con y sin perpetuidad.</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20 text-center">
                <p className="text-[10px] text-violet-400/80 font-medium mb-1">VAN con Perpetuidad</p>
                <p className="text-lg font-bold text-violet-400 tabular-nums" data-testid="dashboard-npv-with-perp">
                  {formatCurrency(valuation?.npv || 0, project?.specifications?.currency || 'MXN')}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-secondary/30 text-center">
                <p className="text-[10px] text-muted-foreground font-medium mb-1">VAN sin Perpetuidad</p>
                <p className="text-lg font-bold tabular-nums" data-testid="dashboard-npv-without-perp">
                  {formatCurrency(valuation_without_perpetuity?.npv || 0, project?.specifications?.currency || 'MXN')}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20 text-center">
                <p className="text-[10px] text-violet-400/80 font-medium mb-1">TIR con Perpetuidad</p>
                <p className="text-lg font-bold text-violet-400 tabular-nums" data-testid="dashboard-irr-with-perp">
                  {formatPercent(valuation?.irr || 0)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-secondary/30 text-center">
                <p className="text-[10px] text-muted-foreground font-medium mb-1">TIR sin Perpetuidad</p>
                <p className="text-lg font-bold tabular-nums" data-testid="dashboard-irr-without-perp">
                  {formatPercent(valuation_without_perpetuity?.irr || 0)}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Valor Terminal: <span className="text-violet-400 font-semibold">{formatCurrency(terminalValue, project?.specifications?.currency || 'MXN')}</span> — representa el valor del negocio operando indefinidamente
            </p>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-6">
        {/* Income vs Expenses Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Ingresos vs Gastos Anuales</CardTitle>
            <p className="text-sm text-muted-foreground">Proyección financiera a {project?.specifications?.project_life_years || 5} años</p>
          </CardHeader>
          <CardContent>
            {hasData && incomeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={incomeChartData} barCategoryGap="20%" margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                  <XAxis dataKey="year" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Legend />
                  <Bar dataKey="Ingresos" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                    {showLabels && (
                      <LabelList dataKey="Ingresos" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={10} />
                    )}
                  </Bar>
                  <Bar dataKey="Gastos" fill="#94A3B8" radius={[4, 4, 0, 0]}>
                    {showLabels && (
                      <LabelList dataKey="Gastos" position="top" formatter={(v) => formatChartValue(v)} fill="#64748B" fontSize={10} />
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Agrega datos al proyecto para ver las proyecciones
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cumulative Cash Flow Chart */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold">Flujo de Caja Acumulado</CardTitle>
                <p className="text-sm text-muted-foreground">Excedente total del ciclo de vida</p>
              </div>
              {breakEvenYear && (
                <span className="px-3 py-1 bg-success/20 text-success text-sm font-medium rounded-full">
                  RENTABLE AÑO {breakEvenYear}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {hasData && cashFlowChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={cashFlowChartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                  <XAxis dataKey="year" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => formatChartValue(v)} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                    formatter={(value, name) => name === 'value' ? [formatCurrency(value), 'Flujo Acumulado'] : [null, null]}
                  />
                  <ReferenceLine y={0} stroke="#64748B" strokeDasharray="4 4" strokeWidth={1} />
                  <defs>
                    <linearGradient id="gradPositive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="gradNegative" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EF4444" stopOpacity={0.05}/>
                      <stop offset="100%" stopColor="#EF4444" stopOpacity={0.4}/>
                    </linearGradient>
                  </defs>
                  <Bar dataKey="value" isAnimationActive={false} barSize={20}>
                    {cashFlowChartData.map((entry, index) => (
                      <Cell 
                        key={index} 
                        fill={entry.value >= 0 ? 'rgba(14,165,233,0.35)' : 'rgba(239,68,68,0.35)'}
                        stroke={entry.value >= 0 ? '#0EA5E9' : '#EF4444'}
                        strokeWidth={1}
                      />
                    ))}
                  </Bar>
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#94A3B8"
                    strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      const color = payload.value >= 0 ? '#0EA5E9' : '#EF4444';
                      return <circle cx={cx} cy={cy} r={4} fill={color} stroke={color} strokeWidth={1} />;
                    }}
                    activeDot={{ r: 6 }}
                  >
                    {showLabels && (
                      <LabelList dataKey="value" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={10} />
                    )}
                  </Line>
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Agrega datos al proyecto para ver el flujo de caja
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Decision Card */}
      <Card className={cn(
        "border-2",
        hasData && valuation?.decision === 'ACEPTADO' ? 'border-success/50 bg-success/5' : 
        hasData ? 'border-destructive/50 bg-destructive/5' : 'border-border/50'
      )}>
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">Veredicto Final</p>
              <h2 className={cn(
                "text-5xl font-bold mb-4",
                hasData && valuation?.decision === 'ACEPTADO' ? 'text-success' : 
                hasData ? 'text-destructive' : 'text-muted-foreground'
              )}>
                {hasData ? (valuation?.decision === 'ACEPTADO' ? 'PROYECTO ACEPTADO' : 'PROYECTO RECHAZADO') : 'SIN DATOS'}
              </h2>
              <p className="text-muted-foreground max-w-xl">
                {hasData ? (
                  valuation?.decision === 'ACEPTADO' 
                    ? `Todos los criterios de inversión se cumplen. El VAN es positivo y la TIR excede la tasa WACC por ${formatPercent(valuation?.irr_vs_wacc || 0)}.`
                    : 'Los criterios de inversión no se cumplen. Revisa los parámetros del proyecto.'
                ) : (
                  'Completa las secciones del proyecto para obtener la valuación.'
                )}
              </p>
            </div>
            <div className={cn(
              "w-24 h-24 rounded-full flex items-center justify-center",
              hasData && valuation?.decision === 'ACEPTADO' ? 'bg-success/20' : 
              hasData ? 'bg-destructive/20' : 'bg-muted'
            )}>
              {hasData && valuation?.decision === 'ACEPTADO' ? (
                <CheckCircle className="w-12 h-12 text-success" />
              ) : hasData ? (
                <XCircle className="w-12 h-12 text-destructive" />
              ) : (
                <DollarSign className="w-12 h-12 text-muted-foreground" />
              )}
            </div>
          </div>
          {hasData && (
            <>
            <div className="mt-6 pt-6 border-t border-border/50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-success" />
                Factibilidad económica confirmada
              </div>
            <div className="flex gap-2">
              <Button 
                className="gap-2" 
                variant="outline"
                onClick={handleExportPdf}
                disabled={exportingPdf}
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4" />
                {exportingPdf ? 'Generando...' : 'PDF'}
              </Button>
              <Button 
                className="gap-2" 
                variant="outline"
                onClick={handleExportFormulaExcel}
                disabled={exportingFormula}
                data-testid="export-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                {exportingFormula ? 'Generando...' : 'Excel'}
              </Button>
              <Button 
                className="gap-2" 
                variant="default"
                onClick={() => setShowPptxModal(true)}
                data-testid="export-pptx-btn"
              >
                <Presentation className="w-4 h-4" />
                Presentación
              </Button>
            </div>
            </div>

          {showPptxModal && (
            <div className="mt-4 p-5 rounded-xl border border-border bg-card/80 space-y-4 animate-fade-in" data-testid="pptx-modal">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">Personaliza tu presentación</h4>
                <button onClick={() => setShowPptxModal(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Color principal</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={pptxSettings.primary_color}
                      onChange={(e) => setPptxSettings(p => ({ ...p, primary_color: e.target.value }))}
                      className="w-9 h-9 rounded border border-border cursor-pointer"
                      data-testid="pptx-primary-color"
                    />
                    <span className="text-xs text-muted-foreground">{pptxSettings.primary_color}</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Color de acento</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={pptxSettings.accent_color}
                      onChange={(e) => setPptxSettings(p => ({ ...p, accent_color: e.target.value }))}
                      className="w-9 h-9 rounded border border-border cursor-pointer"
                      data-testid="pptx-accent-color"
                    />
                    <span className="text-xs text-muted-foreground">{pptxSettings.accent_color}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">URL del logotipo (opcional)</label>
                <input
                  type="url"
                  value={pptxSettings.logo_url}
                  onChange={(e) => setPptxSettings(p => ({ ...p, logo_url: e.target.value }))}
                  placeholder="https://tu-empresa.com/logo.png"
                  className="w-full h-9 rounded-md border border-border bg-background text-sm px-3 focus:outline-none focus:ring-1 focus:ring-ring"
                  data-testid="pptx-logo-url"
                />
              </div>
              <Button
                onClick={handleExportPptx}
                disabled={exportingPptx}
                className="w-full gap-2"
                data-testid="pptx-generate-btn"
              >
                <Presentation className="w-4 h-4" />
                {exportingPptx ? 'Generando presentación...' : 'Generar y Descargar Presentación'}
              </Button>
            </div>
          )}
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
